import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, ShieldCheck } from 'lucide-react';
import { adminUsersAPI } from '../api';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input, Textarea, Select, TagSelect, Table, Pagination, Modal, PageHeader, Alert, useSort } from '../components/UI';

const ROLES = ['superadmin', 'admin', 'operator', 'user'];
// operator & user wajib tenant_ids; user maksimal 1 tenant, operator boleh banyak
const TENANT_SCOPED_ROLES = ['operator', 'user'];
const SINGLE_TENANT_ROLES = ['user'];

const ROLE_COLORS = {
  superadmin: { bg: 'rgba(239,68,68,0.12)',  fg: 'var(--accent-red)',    bd: 'rgba(239,68,68,0.3)' },
  admin:      { bg: 'rgba(139,92,246,0.15)', fg: 'var(--accent-purple)', bd: 'rgba(139,92,246,0.3)' },
  operator:   { bg: 'rgba(59,130,246,0.1)',  fg: 'var(--accent-blue)',   bd: 'rgba(59,130,246,0.25)' },
  user:       { bg: 'rgba(16,185,129,0.12)', fg: 'var(--accent-green)', bd: 'rgba(16,185,129,0.3)' },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS.user;
  return (
    <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, fontWeight: 500 }}>
      {role}
    </span>
  );
}

function StatusBadge({ status }) {
  const active = status === 'active';
  return (
    <span style={{
      fontSize: 11, padding: '2px 8px', borderRadius: 20,
      background: active ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.12)',
      color: active ? 'var(--accent-green)' : 'var(--text-muted)',
      border: `1px solid ${active ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`,
    }}>
      {active ? '● active' : '○ ' + (status || 'inactive')}
    </span>
  );
}

const MenuItem = ({ children, onClick, color }) => (
  <button onClick={onClick} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)',
    transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 160, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const EMPTY_FORM = {
  username: '', password: '', email: '', fullname: '', description: '',
  role: 'user', status: 'active', tenant_ids: [],
};

const RowMenuButton = ({ row, openMenuId, setOpenMenuId, onlyDeleted, openEdit, handleRestore, setDeleteConfirm, canDelete = true }) => {
  const btnRef = React.useRef(null);
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === row.id ? null : row.id); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>
        ···
      </button>
      <DropdownPortal anchorRef={btnRef} open={openMenuId === row.id}>
        {onlyDeleted ? (
          <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(row); setOpenMenuId(null); }}>↩ Restore</MenuItem>
        ) : (
          <MenuItem onClick={() => { openEdit(row); setOpenMenuId(null); }}>✏️ Edit</MenuItem>
        )}
        {!onlyDeleted && canDelete && <div style={{ borderTop: '1px solid var(--border)' }} />}
        {!onlyDeleted && canDelete && (
          <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(row); setOpenMenuId(null); }}>🗑 Hapus</MenuItem>
        )}
      </DropdownPortal>
    </div>
  );
};

export default function AdminUsers() {
  const { tenants } = useTenant();
  const { user: me } = useAuth();
  const myRole = me?.role;
  const canManage = myRole === 'superadmin' || myRole === 'admin';

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [onlyDeleted, setOnlyDeleted] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (onlyDeleted) params.with_deleted = true;
      if (sortParam) params.sort = sortParam;
      const res = await adminUsersAPI.list(params);
      let list = res.admin_users || [];
      if (onlyDeleted) list = list.filter(r => !!r.deleted_at);
      setRows(list);
      setTotal(res.total || list.length);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, perPage, q, roleFilter, statusFilter, onlyDeleted, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [onlyDeleted, roleFilter, statusFilter]);

  const isTenantScoped = (role) => TENANT_SCOPED_ROLES.includes(role);
  const isSingleTenant = (role) => SINGLE_TENANT_ROLES.includes(role);

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setPasswordConfirm('');
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditTarget(row);
    setForm({
      username: row.username || '',
      password: '',
      email: row.email || '',
      fullname: row.fullname || '',
      description: row.description || '',
      role: row.role || 'user',
      status: row.status || 'active',
      tenant_ids: row.tenant_ids || [],
    });
    setPasswordConfirm('');
    setFormError(null);
    setModalOpen(true);
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleRoleChange = (role) => {
    setForm(p => {
      let tenant_ids = p.tenant_ids;
      if (!TENANT_SCOPED_ROLES.includes(role)) tenant_ids = [];
      else if (SINGLE_TENANT_ROLES.includes(role) && tenant_ids.length > 1) tenant_ids = tenant_ids.slice(0, 1);
      return { ...p, role, tenant_ids };
    });
  };

  const handleSave = async () => {
    setFormError(null);

    if (!form.username.trim()) return setFormError('Username wajib diisi.');
    if (!editTarget && !form.password) return setFormError('Password wajib diisi.');
    if (form.password && form.password.length < 8) return setFormError('Password minimal 8 karakter.');
    if (form.password && form.password !== passwordConfirm) return setFormError('Konfirmasi password tidak cocok.');
    if (canManage && isTenantScoped(form.role) && (!form.tenant_ids || form.tenant_ids.length === 0)) {
      return setFormError(`tenant_ids wajib diisi untuk role ${form.role}.`);
    }
    if (canManage && isSingleTenant(form.role) && form.tenant_ids.length > 1) {
      return setFormError('Role user maksimal 1 tenant.');
    }

    setSaving(true);
    try {
      const payload = {
        email: form.email,
        fullname: form.fullname,
        description: form.description,
      };
      if (canManage) {
        payload.role = form.role;
        payload.status = form.status;
        // tenant_ids hanya relevan & dikirim untuk role yang tenant-scoped (operator/user)
        payload.tenant_ids = isTenantScoped(form.role) ? form.tenant_ids : [];
      }
      if (form.password) payload.password = form.password;

      if (editTarget) {
        await adminUsersAPI.update(editTarget.id, payload);
        setSuccess('Admin user diperbarui.');
      } else {
        await adminUsersAPI.create({ ...payload, username: form.username });
        setSuccess('Admin user dibuat.');
      }
      setModalOpen(false);
      load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminUsersAPI.delete(deleteConfirm.id);
      setSuccess(`Admin user "${deleteConfirm.username}" dihapus.`);
      setDeleteConfirm(null);
      load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (row) => {
    try {
      await adminUsersAPI.restore(row.id);
      setSuccess('Admin user dipulihkan.');
      load();
    } catch (e) { setError(e.message); }
  };

  const tenantNames = (ids) => {
    if (!ids || ids.length === 0) return '—';
    return ids.map(id => tenants.find(t => t.id === id)?.name || `#${id}`).join(', ');
  };

  const columns = [
    {
      key: 'username', label: 'Username', sortable: true, render: (v, row) => (
        <div>
          <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
            {v} {me?.id === row.id && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>(Anda)</span>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.fullname}</div>
        </div>
      )
    },
    { key: 'email', label: 'Email', sortable: true, render: v => <span style={{ fontSize: 12 }}>{v || '—'}</span> },
    { key: 'role', label: 'Role', sortable: true, render: v => <RoleBadge role={v} /> },
    {
      key: 'tenant_ids', label: 'Tenant', render: (v, row) =>
        isTenantScoped(row.role)
          ? <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{tenantNames(v)}</span>
          : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>— (semua)</span>
    },
    { key: 'status', label: 'Status', sortable: true, render: v => <StatusBadge status={v} /> },
    { key: 'last_login_at', label: 'Last Login', sortable: true, render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v ? new Date(v).toLocaleString('id-ID') : '—'}</span> },
    {
      key: 'id', label: '', render: (id, row) => {
        const isSelf = me?.id === row.id;
        if (!canManage && !isSelf) return null;
        if (!canManage && onlyDeleted) return null; // operator/user tidak punya akses ke tab terhapus
        return (
          <RowMenuButton row={row} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} onlyDeleted={onlyDeleted}
            openEdit={openEdit} handleRestore={handleRestore} setDeleteConfirm={setDeleteConfirm} canDelete={canManage} />
        );
      }
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="Admin Users"
        subtitle={`${total} akun${onlyDeleted ? ' (terhapus)' : ' terdaftar'}`}
        actions={
          canManage ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
                {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
              </Button>
              <Button onClick={openCreate}><Plus size={14} /> Tambah Admin User</Button>
            </div>
          ) : null
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari username, email, nama…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 260 }} />
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Role</option>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={rows} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada admin user'} />
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Admin User' : 'Tambah Admin User'} width={480}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Username *" value={form.username} disabled={!!editTarget}
            onChange={e => f('username', e.target.value)} placeholder="johndoe" />
          <Input label="Nama Lengkap" value={form.fullname} onChange={e => f('fullname', e.target.value)} placeholder="John Doe" />
          <Input label="Email" type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="john@example.com" />
          <Textarea label="Description" value={form.description} onChange={e => f('description', e.target.value)} rows={3} />

          {canManage ? (
            <>
              <Select label="Role *" value={form.role} onChange={e => handleRoleChange(e.target.value)}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </Select>

              {/* tenant_ids wajib untuk role operator (multi) & user (maks 1) */}
              {isTenantScoped(form.role) && (
                <TagSelect
                  label={`Tenant *`}
                  value={form.tenant_ids}
                  onChange={ids => f('tenant_ids', ids)}
                  options={tenants}
                  getKey={t => t.id}
                  getLabel={t => t.name}
                  getSub={t => t.realm}
                  maxTags={isSingleTenant(form.role) ? 1 : undefined}
                  placeholder="Cari & pilih tenant..."
                  hint={isSingleTenant(form.role)
                    ? 'Role user hanya bisa diarahkan ke 1 tenant.'
                    : 'Operator hanya bisa mengakses tenant yang dipilih di sini.'}
                />
              )}
              {!isTenantScoped(form.role) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', padding: '4px 2px' }}>
                  <ShieldCheck size={13} /> Role <strong>{form.role}</strong> memiliki akses ke semua tenant — tidak perlu memilih tenant.
                </div>
              )}

              <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--text-muted)' }}>
              Role &amp; tenant hanya bisa diubah oleh admin/superadmin.
            </div>
          )}

          <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />

          <Input label={editTarget ? 'Password Baru (kosongkan jika tidak diubah)' : 'Password *'}
            type="password" value={form.password} onChange={e => f('password', e.target.value)}
            placeholder={editTarget ? '••••••••' : 'Min. 8 karakter'} />
          {(form.password || !editTarget) && (
            <Input label="Konfirmasi Password" type="password" value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)} placeholder="Ulangi password" />
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Buat Admin User'}</Button>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus Admin User" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus admin user <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.username}</strong>?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>
    </div>
  );
}